import {
  AssessmentStageActionKind,
  CognitiveKind,
  ProjectPageGetProjectByIdDocument,
  StageActionAvailability,
  StageAvailableActionFragment,
  useAddCognitiveTestToAssessmentStageMutation,
  useAddPersonalityTestToAssessmentStageMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import {
  Button,
  SelectFormField,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { cognitiveKindLabel, cognitiveTestLabel } from '../helpers';

export type TAddTestActionMode = 'personality' | 'cognitive';

const ACTION_KIND_BY_COGNITIVE_KIND: Record<
  CognitiveKind,
  AssessmentStageActionKind
> = {
  [CognitiveKind.Inductive]: AssessmentStageActionKind.CognitiveTestInductive,
  [CognitiveKind.Numerical]: AssessmentStageActionKind.CognitiveTestNumerical,
  [CognitiveKind.Verbal]: AssessmentStageActionKind.CognitiveTestVerbal,
};

export const isTestActionAvailable = (
  availableActions: StageAvailableActionFragment[],
  kind: AssessmentStageActionKind
): boolean =>
  availableActions.find(actionAvailability => actionAvailability.kind === kind)
    ?.availability === StageActionAvailability.Available;

interface IAddTestAction {
  stageId: string;
  projectId: number;
  mode: TAddTestActionMode;
  availableActions: StageAvailableActionFragment[];
  onClose: () => void;
}

const AddTestAction: FC<IAddTestAction> = ({
  stageId,
  projectId,
  mode,
  availableActions,
  onClose,
}) => {
  const { handleMsgType } = useNotification();
  const [submitting, setSubmitting] = useState(false);
  const [selectedKind, setSelectedKind] = useState<
    TSelectOption<CognitiveKind> | undefined
  >();

  const label =
    mode === 'personality'
      ? 'Personality test'
      : selectedKind?.value
      ? cognitiveTestLabel(selectedKind.value)
      : 'Cognitive test';

  const cognitiveKindOptions = Object.values(CognitiveKind).map(kind => ({
    label: cognitiveKindLabel(kind),
    value: kind,
    isDisabled: !isTestActionAvailable(
      availableActions,
      ACTION_KIND_BY_COGNITIVE_KIND[kind]
    ),
  }));

  const mutationOptions = {
    refetchQueries: [
      { query: ProjectPageGetProjectByIdDocument, variables: { id: projectId } },
    ],
    onError: (error: { message?: string }) => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message || `${label} could not be added.`,
      });
      setSubmitting(false);
    },
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        message: `${label} added!`,
      });
      onClose();
    },
  };

  const [addPersonalityTest] =
    useAddPersonalityTestToAssessmentStageMutation(mutationOptions);
  const [addCognitiveTest] =
    useAddCognitiveTestToAssessmentStageMutation(mutationOptions);

  const handleAdd = () => {
    if (mode === 'personality') {
      setSubmitting(true);
      addPersonalityTest({ variables: { stageId } });
      return;
    }
    if (!selectedKind?.value) return;
    setSubmitting(true);
    addCognitiveTest({
      variables: { stageId, kind: selectedKind.value },
    });
  };

  const canAdd = mode === 'personality' || Boolean(selectedKind?.value);

  return (
    <>
      {mode === 'cognitive' && (
        <div className="add-stage-action-modal__header">
          <SelectFormField<CognitiveKind>
            id="add-cognitive-test-kind-select"
            label="Cognitive test type"
            placeholder="Cognitive test type"
            options={cognitiveKindOptions}
            value={selectedKind}
            onChange={(option: TSelectOption<CognitiveKind>) =>
              setSelectedKind(option)
            }
            hasNullValue={false}
          />
        </div>
      )}
      <div className="add-stage-action-modal__actions">
        <Button onClick={onClose} size="medium" variant="secondary">
          Cancel
        </Button>
        <Button
          size="medium"
          onClick={handleAdd}
          loading={submitting}
          disabled={submitting || !canAdd}
        >
          Add action
        </Button>
      </div>
    </>
  );
};

export default AddTestAction;
